import React from "react";
import {observer} from "mobx-react-lite";
import type {AxiosResponse} from "axios";
import type {GridColDef, GridSortModel} from "@mui/x-data-grid-premium";
import {Typography} from "@mui/material";
import {useStores} from "~/stores";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid";
import type {AdvancedDashboardPageResponse} from "~/services/ApiTypes";
import type {RequestManager} from "~/stores/managers/RequestManager";
import {ADV_DASH_PAGE_SIZE_OPTIONS} from "~/stores/managers/BagAdvancedInsightsManager";
import {GridDensityButton} from "./GridDensityButton";

export type OrderingPrefix = 'opportunity_' | 'sup_breakdown_' | 'bu_pivot_';

type Row = Record<string, unknown>;

type Props<R extends Row> = {
    layoutKey: string
    columns: GridColDef[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    requestManager: RequestManager<any, AxiosResponse<AdvancedDashboardPageResponse<R>>>
    page: number
    onPageChange: (page: number) => void
    perPage: number
    onPerPageChange: (perPage: number) => void
    orderingPrefix: OrderingPrefix
    /** e.g. n => `${n} categories` */
    countLabel: (count: number) => string
    /** Filter the page on the row; the row is undefined when clicking the only row (clears the filter) */
    onRowClick?: (row: R | undefined) => void
    isRowSelected?: (row: R) => boolean
    noRowsLabel?: string
}

function sortModelOf(ordering: string): GridSortModel {
    if (!ordering) return [];
    return ordering.startsWith('-')
        ? [{field: ordering.substring(1), sort: 'desc'}]
        : [{field: ordering, sort: 'asc'}];
}

/**
 * A server-paged, server-sorted table of the Spend Landscape page on the shared data grid.
 */
export const SpendLandscapeGrid = observer(<R extends Row>({
                                                               layoutKey,
                                                               columns,
                                                               requestManager,
                                                               page,
                                                               onPageChange,
                                                               perPage,
                                                               onPerPageChange,
                                                               orderingPrefix,
                                                               countLabel,
                                                               onRowClick,
                                                               isRowSelected,
                                                               noRowsLabel,
                                                           }: Props<R>) => {
    const {bagStore} = useStores();
    const manager = bagStore.advanced;
    const data = requestManager.result?.data;
    const sourceRows = data?.rows ?? [];
    const rows = sourceRows.map((r, i) => ({...r, __rowId: `${page}-${i}`}));
    const count = data?.count ?? sourceRows.length;

    return <CustomDataGrid
        layoutKey={layoutKey}
        autoHeight
        rows={rows}
        columns={columns}
        getRowId={r => r.__rowId}
        loading={requestManager.busy}
        rowCount={count}
        paginationMode="server"
        sortingMode="server"
        paginationModel={{page, pageSize: perPage}}
        onPaginationModelChange={m => {
            if (m.pageSize !== perPage) onPerPageChange(m.pageSize);
            else if (m.page !== page) onPageChange(m.page);
        }}
        pageSizeOptions={ADV_DASH_PAGE_SIZE_OPTIONS}
        sortModel={sortModelOf(manager.getOrderingWithPrefix(orderingPrefix))}
        onSortModelChange={m => {
            const s = m[0];
            manager.setOrderingWithPrefix(s ? (s.sort === 'desc' ? '-' : '') + s.field : '', orderingPrefix);
        }}
        disableRowSelectionOnClick
        headerFiltersToggle={false}
        showToolbar
        toolbarProps={{
            showFilterPanel: false,
            children: <>
                <Typography variant="body2" sx={{pl: 0.5}} data-testid={`${layoutKey}-count`}>
                    {requestManager.result ? countLabel(count) : ''}
                </Typography>
                <span style={{flexGrow: 1}}/>
                <GridDensityButton/>
            </>,
        }}
        onRowClick={onRowClick ? params => {
            const row = params.row as R;
            onRowClick(sourceRows.length === 1 ? undefined : row);
        } : undefined}
        getRowClassName={params => [
            onRowClick ? 'spend-landscape-row-clickable' : '',
            isRowSelected?.(params.row as R) ? 'spend-landscape-row-selected' : '',
        ].join(' ')}
        localeText={{noRowsLabel: noRowsLabel ?? 'No data in these filters'}}
    />;
}) as <R extends Row>(props: Props<R>) => React.ReactElement;
